// src/services/mediaResolve.js — which decoder reads a media message, what it can
// fetch, and whether an answer we already gave was about a different thing.
//
// 🟢 THESE ARE RULES, NOT RENDERING. They lived inside ChatWindow, which is thirty
//    times their size and cannot be loaded on its own — so the three questions the
//    media pipeline actually asks had no way to be answered in a test. Every one
//    of the faults recorded below was a wrong answer to one of them.
import { decryptMediaUrl, guessMime, cachedMediaUrl } from '../crypto/media';
import { isExportedEntry } from './historySync';

// A synced history entry from the phone: text already has _plain; media has the cloud
// URL + symmetric {k,n} shipped by the phone → fetch+decrypt to a render-ready blob URL.
export async function normalizeSynced(m, opts = {}) {
  const out = { ...m };
  if (m.type === 'images' && Array.isArray(m.images)) {
    const keys = m.mediaKeys;
    /* 🟥 A PLAINTEXT ALBUM CAME OUT EMPTY HERE TOO. decryptMessage learned this
       rule and this function never did: a tile with no key is unreadable
       ciphertext only if the MESSAGE was sealed. An official album is exported
       with `mediaKeys: null` — the absence of keys is what marks those urls
       plaintext — and throwing them away drew a card of tiles that never
       resolved. One rule, and it has to be in both places or the browser
       contradicts itself depending on where the same photo came from. */
    /* 🔒 …and an album the phone could not unseal is exported with `mediaKeys: null` too: the
       e2ee mark (or any key scheme on the row) says those urls are CIPHERTEXT, never plaintext */
    const sealed = (Array.isArray(keys) && keys.length > 0) || m.e2eeMedia === true || !!(m.batchPerDeviceKeys || m.batchEncryptionKey || m.groupBatchEncryptionData);
    out._mediaBatch = await Promise.all(m.images.map(async (url, i) => {
      const mk = Array.isArray(keys) ? keys[i] : null;
      const kind = mk?.type === 'video' || /\.(mp4|mov)/i.test(url) ? 'video' : 'image';
      if (opts.skipVideo && kind === 'video') return { kind, url: null, needsLoad: true };
      /* 🟥 SAME ISOLATION AS THE LIVE PATH (services/messages.js). A Promise.all
         over an album turns one unreadable tile into a card of spinners. */
      if (url && mk?.k && mk?.n) {
        try {
          return { kind, url: await decryptMediaUrl(url, mk.k, mk.n, guessMime(url, kind)) };
        } catch (e) {
          console.warn(`[media] synced album tile ${i} failed (${e?.name || 'error'})`, url, e);
          return { kind, url: null };
        }
      }
      return { kind, url: sealed ? null : (url || null) };
    }));
    return out;
  }
  if (opts.skipVideo && m.type === 'video' && m.url) {
    /* 🟥 THE BUTTON CAME BACK ON EVERY VISIT, FOR A CLIP ALREADY DECRYPTED.
       `_media` is state, and state does not survive leaving the chat — so a
       video watched an hour ago asked to be loaded again, and again after that.
       The bytes are on disk now (services/mediaStore.js), so the question the
       button asks — may I download tens of megabytes — has already been
       answered, and asking it again is asking permission for work that is not
       going to happen. */
    const have = await cachedMediaUrl(m.url, m.mediaKey?.k);
    if (have) { out._media = have; return out; }
    /* 🟢 AND A PLAINTEXT CLIP NEVER NEEDED THE BUTTON AT ALL. With no key there
       is nothing to fetch and nothing to decrypt: the url IS the source, and
       `preload="metadata"` means the browser takes the header and stops. The
       button was guarding a cost that only exists for sealed media. */
    if (!m.mediaKey?.k) { out._media = m.url; return out; }
    out._needsLoad = true;
    return out;
  }
  if (m.url && m.mediaKey?.k && m.mediaKey?.n) {
    const kind = m.type === 'video' ? 'video' : m.type === 'audio' ? 'audio' : 'image';
    out._media = await decryptMediaUrl(m.url, m.mediaKey.k, m.mediaKey.n, guessMime(m.url, kind));
  } else if (m.url) {
    out._media = m.url;
  }
  // 🟢 A DOCUMENT'S FIRST PAGE IS A SECOND UPLOAD WITH ITS OWN KEY, and the phone's
  //    export ships it beside the file (docThumbUrl + docThumbKey). Without this branch a
  //    document that arrived through history sync could only ever be an icon, however
  //    good a preview the sender had made.
  if (m.type === 'document' && m.docThumbUrl && m.docThumbKey?.k && m.docThumbKey?.n) {
    try {
      out._docThumb = await decryptMediaUrl(m.docThumbUrl, m.docThumbKey.k, m.docThumbKey.n, 'image/png');
    } catch { /* the icon card is a fine document bubble */ }
  }
  return out;
}

/** Is there anything for THIS browser to fetch? Media the phone already downloaded
 *  points at a local file path in its own row, and store-and-forward has usually
 *  deleted the 1:1 message doc by then — so the sealed export carries no URL. Checking
 *  first means we neither queue a fetch that cannot succeed nor offer a Retry that can
 *  only fail. */
/* ═══════════════════════════════════════════════════════════════════════════
   🟥 A FAILURE WAS LATCHED AGAINST THE FIRST COPY WE HAPPENED TO SEE.

   `requestedRef` is keyed by message id and `_mediaResolved` survives every
   repaint, so one attempt is all a message ever gets. That is right while the
   message is the same message — and the merged list is assembled from TWO
   sources that arrive milliseconds apart: the phone's export paints first
   (it is already in memory), the live Firestore documents land after. So the
   queue could resolve a photo against a copy that had no url in a field it
   understood, latch the failure, and then sit in front of a perfectly good copy
   for the rest of the session.

   That is the "click Try again and it works" half of the report, and the
   "exit and re-enter and it goes back" half: the button wins the race by
   running after both copies have arrived, and reopening runs it again.

   🟢 SO THE ATTEMPT IS REMEMBERED AGAINST WHAT IT WAS AN ATTEMPT AT. When the
      urls or the key-presence of a message change, the old answer was about a
      different thing and is dropped. It cannot loop: the signature only moves
      when the message's own source fields do.
   ═══════════════════════════════════════════════════════════════════════════ */
export function mediaSig(m) {
  if (!m) return '';
  if (m.type === 'images') {
    return `images§${(m.images || []).join('|')}§${m.mediaKeys ? 'k' : '-'}${m.batchPerDeviceKeys ? 'b' : '-'}§${isExportedEntry(m) ? 'x' : 'l'}`;
  }
  const url = m.url || m.imageUrl || m.videoUrl || m.audioUrl || m.documentUrl || '';
  const thumb = m.documentThumbUrl || m.docThumbUrl || '';
  const keyed = (m.mediaKey || m.imagePerDeviceKeys || m.videoPerDeviceKeys
    || m.audioPerDeviceKeys || m.documentPerDeviceKeys) ? 'k' : '-';
  /* 🟥 THE VOCABULARY IS PART OF THE SIGNATURE. The export and the live document
     can carry the SAME url under different field names — that is the whole bug
     these two copies caused — so a signature that reads only the value cannot
     tell the two attempts apart, and the second copy would never earn one. */
  return `${m.type || ''}§${url}§${thumb}§${keyed}§${isExportedEntry(m) ? 'x' : 'l'}`;
}

export function hasCloudSource(m) {
  const http = (u) => typeof u === 'string' && /^https?:/i.test(u);
  if (m.type === 'images') return (m.images || []).some(http);
  // 🟥 A DOCUMENT'S FIRST PAGE IS A SEPARATE UPLOAD, AND IT COUNTS. This used to ask
  //    only about the FILE. A document whose file URL had been swept but whose
  //    thumbnail was still there was declared "device only" — which sets
  //    _mediaResolved and adds the id to requestedRef, so the preview was never
  //    fetched and never retried. Permanently an icon, for a first page that was
  //    sitting in the cloud the whole time.
  if (m.type === 'document' && (http(m.documentThumbUrl) || http(m.docThumbUrl))) return true;
  return http(m.url || m.imageUrl || m.videoUrl || m.audioUrl || m.documentUrl);
}


/* ═══════════════════════════════════════════════════════════════════════════
   🟥 A SECOND ATTEMPT MUST NOT BE ALLOWED TO MAKE THINGS WORSE.

   `mediaSig` exists so a message whose copy improved earns another attempt, and
   that is right. What it did not say is which way the copy moved. Both copies of
   a media message are on screen within a second of each other — the phone's
   PLAINTEXT export first, the sealed Firestore document after — and after this
   browser has re-linked with a new key the second one is a padlock it will never
   open. So the "better copy" was the worse copy, the queue resolved against it,
   and the answer it wrote back was `_media: null` on top of a photo that was
   already rendering. That is the sub-second flicker in the report, exactly.

   🟢 SO THE ATTEMPT IS STILL MADE, AND THE RESULT IS ONLY TAKEN IF IT IS NOT A
      DOWNGRADE. Retrying a FAILURE is the whole point of the re-arm and stays;
      overwriting a SUCCESS never was. One rule covers every route a downgrade
      could arrive by, including the ones nobody has written yet.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Does this row already hold a usable answer? Albums count tile by tile. */
export function hasResolvedMedia(m) {
  if (!m) return false;
  if (m._media) return true;
  if (Array.isArray(m._mediaBatch)) return m._mediaBatch.some((t) => t && t.url);
  return false;
}

/** …and is that answer COMPLETE? A half-filled album still deserves another go. */
export function fullyResolvedMedia(m) {
  if (!m) return false;
  if (Array.isArray(m._mediaBatch)) {
    return m._mediaBatch.length > 0
      && m._mediaBatch.every((t) => !t || t.url || t.needsLoad);
  }
  return !!m._media;
}

/**
 * Apply a fresh resolution to a row without ever losing ground.
 * @param prev the row as it stands (may already be showing a picture)
 * @param done what this attempt produced
 */
export function mergeResolved(prev, done) {
  const out = { ...(prev || {}), ...(done || {}) };
  /* 🟥 `{...a, ...b}` COPIES AN UNDEFINED b INTO a — a spread does not skip a
     key just because its value is missing, so the failing copy erased the good
     url whether it said `null`, `undefined`, or nothing at all. */
  if (prev?._media && !done?._media) out._media = prev._media;
  if (prev?._docThumb && !done?._docThumb) out._docThumb = prev._docThumb;
  if (prev?._caption != null && done?._caption == null) out._caption = prev._caption;
  if (prev?._plain != null && done?._plain == null) out._plain = prev._plain;

  const before = Array.isArray(prev?._mediaBatch) ? prev._mediaBatch : null;
  const after = Array.isArray(done?._mediaBatch) ? done._mediaBatch : null;
  if (before && after) {
    /* 🟢 TILE BY TILE, because an album is not one answer. The unreadable copy
       of a six-photo card produces six nulls, and a whole-array replacement
       throws away five pictures to report one failure. */
    const n = Math.max(before.length, after.length);
    const merged = [];
    for (let i = 0; i < n; i += 1) {
      const a = after[i];
      const b = before[i];
      merged.push((a && a.url) ? a : ((b && b.url) ? b : (a || b || null)));
    }
    out._mediaBatch = merged;
  } else if (before && !after) {
    out._mediaBatch = before;
  }
  return out;
}
